/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ExpertContent,
  ExpertUploadMediaParam,
  FetchFolderSelectorMediaParams,
  FetchFolderSelectorMediaResponse,
  IMediaUpdate,
  MediaLibraryState,
  MediaResponse,
  MoveMediaParam,
  RemainingStorage,
  SaveDownloadVideoTrimmedParams,
  SelectedFolderData,
  Sort,
} from './interface';

export const initialState: MediaLibraryState = {
  loading: false,
  modalLoading: false,
  filters: {
    keyword: '',
    type: [],
    sortBy: Sort.UPLOADED,
  },
  count: 0,
  page: 0,
  perPage: 12,
  filtersApplied: false,
  path: '/root',
  selectedFiles: [],
  media: [],
  mediaWithSubFolders: [],
  total: 0,
  stroage: {
    allowedStorage: 0,
    mediaStorageSize: 0,
    remainingStorage: 0,
  },
  renameMediaFormOpen: false,
  openFolderSelect: false,
  selectedTargetFolder: null,
  isDownloadMediaEnabled: null,
  undoButtonDisabled: true,
  selectedFilesForUndo: [],
  saveAndShareRecording: null,
  expandedMediaItems: [],
};

export const mediaLibrarySlice = createSlice({
  name: '/media-library',
  initialState,
  reducers: {
    setCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
    },
    setPath: (state, action: PayloadAction<string>) => {
      state.page = 0;
      state.path = action.payload;
    },
    startLoading: (state) => {
      state.loading = true;
    },
    endLoading: (state) => {
      state.loading = false;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.perPage = action.payload;
    },
    setKeyword: (state, action: PayloadAction<string>) => {
      state.page = 0;
      state.filters.keyword = action.payload;

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    setSortBy: (state, action: PayloadAction<Sort>) => {
      state.page = 0;
      state.filters.sortBy = action.payload;

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    setType: (state, action: PayloadAction<string>) => {
      state.page = 0;

      if (state.filters.type.includes(action.payload)) {
        state.filters.type = state.filters.type.filter((i) => i !== action.payload);
      } else {
        state.filters.type = [...state.filters.type, action.payload];
      }

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    setSelectedTargetFolder: (state, action: PayloadAction<SelectedFolderData | null>) => {
      state.selectedTargetFolder = action.payload;
    },
    setisDownloadMediaEnabled: (state, action: PayloadAction<boolean | null>) => {
      state.isDownloadMediaEnabled = action.payload;
    },
    setOpenFolderSelect: (state, action: PayloadAction<boolean>) => {
      state.openFolderSelect = action.payload;
      if (action.payload === false) {
        state.expandedMediaItems = [];
      }
    },
    setRenameMediaFormOpen: (state, action: PayloadAction<boolean>) => {
      state.renameMediaFormOpen = action.payload;
    },
    selectUndoButtonDisabled: (state, action: PayloadAction<boolean>) => {
      state.undoButtonDisabled = action.payload;
    },
    setSaveAndShareRecording: (state, action: PayloadAction<boolean | null>) => {
      state.saveAndShareRecording = action.payload;
    },
    toggleExpanded: (state, action: PayloadAction<string>) => {
      state.expandedMediaItems = state.expandedMediaItems.includes(action.payload)
        ? state.expandedMediaItems.filter((i) => i !== action.payload)
        : [...state.expandedMediaItems, action.payload];
    },
    clearFilters: (state) => {
      state.page = 0;
      state.filters = {
        keyword: '',
        type: [],
        sortBy: Sort.UPLOADED,
      };

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    fetchMedia(state: MediaLibraryState) {
      state.loading = true;
    },
    fetchMediaSucceed(state: MediaLibraryState, action: PayloadAction<MediaResponse>) {
      state.loading = false;
      state.media = action.payload.result;
      state.total = action.payload.total;
    },
    fetchMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    fetchMediaWithSubFolders(state: MediaLibraryState) {
      state.loading = true;
    },
    fetchMediaWithSubFoldersSucceed(state: MediaLibraryState, action: PayloadAction<any[]>) {
      state.loading = false;
      state.mediaWithSubFolders = action.payload;
    },
    fetchMediaWithSubFoldersFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    fetchFolderSelectorMedia(state: MediaLibraryState, _action: PayloadAction<FetchFolderSelectorMediaParams | undefined>) {
      state.loading = true;
    },
    fetchFolderSelectorMediaSucceed(state: MediaLibraryState, action: PayloadAction<FetchFolderSelectorMediaResponse>) {
      state.loading = false;
      if (action.payload.parentFolderId !== '') {
        const parentFolder = state.mediaWithSubFolders.find((folder) => folder._id === action.payload.parentFolderId);
        if (parentFolder) {
          parentFolder.subFolders = action.payload.result;
        }
      } else {
        state.mediaWithSubFolders = action.payload.result;
      }
    },
    fetchFolderSelectorMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    clearMediaWithSubFolders(state: MediaLibraryState) {
      state.mediaWithSubFolders = [];
    },
    fetchRemainingStroage(state: MediaLibraryState) {
      state.loading = true;
    },
    fetchRemainingStroageFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    fetchRemainingStroageSucceed(
      state: MediaLibraryState,
      action: PayloadAction<RemainingStorage>,
    ) {
      const stroage = action.payload;
      state.loading = false;
      state.stroage = stroage;
    },
    uploadMedia(state: MediaLibraryState, _action: PayloadAction<ExpertUploadMediaParam>) {
      state.loading = true;
    },
    uploadMediaSucceed(state: MediaLibraryState, _action: PayloadAction<ExpertContent>) {
      state.loading = false;
      state.selectedTargetFolder = null;
    },
    uploadMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    moveMedia(
      state: MediaLibraryState,
      _action: PayloadAction<MoveMediaParam | undefined>,
    ) {
      state.loading = true;
    },
    moveMediaSucceed(state: MediaLibraryState) {
      state.loading = false;
    },
    moveMediaFailed(state: MediaLibraryState) {
      state.loading = false;
      state.selectedTargetFolder = null;
    },
    undoMediaMove(state: MediaLibraryState) {
      state.loading = true;
    },
    undoMediaMoveSucceed(state: MediaLibraryState) {
      state.loading = false;
    },
    undoMediaMoveFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    deleteMedia(
      state: MediaLibraryState,
      action: PayloadAction<{ id: string; contentUrl: string | null; fileSize: number }>,
    ) {
      state.loading = true;
    },
    deleteMediaSucceed(state: MediaLibraryState, _action: PayloadAction<ExpertContent>) {
      state.loading = false;
    },
    deleteMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    deleteSelectedMedia(state: MediaLibraryState) {
      state.loading = true;
    },
    deleteSelectedMediaSucceed(state: MediaLibraryState) {
      state.loading = false;
    },
    deleteSelectedMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    selectAllFiles: (state) => {
      state.selectedFiles = state.media.map((i) => i._id);
      state.selectedFilesForUndo = state.media.map((i) => ({
        id: i._id,
        path: i.path ?? '/root',
        title: i.title,
      }));
    },
    selectMediaItem: (state: MediaLibraryState, action: PayloadAction<{ id: string, path: string, title: string }>) => {
      state.selectedFiles = [...state.selectedFiles, action.payload.id];
      state.selectedFilesForUndo = [...state.selectedFilesForUndo, { id: action.payload.id, path: action.payload.path, title: action.payload.title }];
    },
    deselectAllFiles: (state) => {
      state.selectedFiles = [];
    },
    deselectFilesSelectedForUndo: (state) => {
      state.selectedFilesForUndo = [];
    },
    toggleSelectedFile: (state, action: PayloadAction<string>) => {
      if (state.selectedFiles.includes(action.payload)) {
        state.selectedFiles = state.selectedFiles.filter((i) => i !== action.payload);
      } else {
        state.selectedFiles = [...state.selectedFiles, action.payload];
      }
    },
    toggleSelectedFilesForUndo: (state, action: PayloadAction<{ id: string, path: string, title: string }>) => {
      const { id, path, title } = action.payload;
      const isSelected = state.selectedFilesForUndo.some((file) => file.id === id);
      if (isSelected) {
        state.selectedFilesForUndo = state.selectedFilesForUndo.filter((file) => file.id !== id);
      } else {
        state.selectedFilesForUndo = [...state.selectedFilesForUndo, { id, path, title }];
      }
    },
    clearSelectedFilesForUndo: (state) => {
      state.selectedFilesForUndo = [];
    },
    postUpdate: (state) => {
      let filtersApplied = false;

      if (state.filters.keyword.length > 0) {
        filtersApplied = true;
      }

      if (state.filters.type.length > 0) {
        filtersApplied = true;
      }

      if (state.filters.sortBy !== Sort.AZ) {
        filtersApplied = true;
      }

      state.filtersApplied = filtersApplied;
    },
    updateMedia(state: MediaLibraryState, _action: PayloadAction<IMediaUpdate>) {
      state.loading = true;
    },
    updateMediaSucceed(state: MediaLibraryState, _action: PayloadAction<IMediaUpdate>) {
      state.loading = false;
    },
    updateMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    saveDownloadTrimmedVideo(
      state: MediaLibraryState,
      _action: PayloadAction<SaveDownloadVideoTrimmedParams>,
    ) {
      state.modalLoading = true;
    },
    saveDownloadTrimmedVideoSuccess(state: MediaLibraryState) {
      state.modalLoading = false;
    },
    saveDownloadTrimmedVideoFailed(state: MediaLibraryState) {
      state.modalLoading = false;
    },
  },
});

export const { actions: mediaLibraryAction } = mediaLibrarySlice;
